'use client';
import { IconMoneybag, IconSignature } from '@tabler/icons-react';
import { OfferStatus } from 'database';
import { format } from 'date-fns';
import { useTranslations } from 'i11n';
import React from 'react';
import {
  Badge,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  Separator,
  cn,
} from 'ui';
import { FileListItem, FileListLabel } from './File';
import {
  OfferDetailField,
  OfferDetailGrid,
  OfferDetailValue,
  OfferFileLink,
} from './OfferDetailField';
import { ClaimOfferMinimalDetail } from 'bff';
import { NonUndefined } from 'react-hook-form';
import type { ClaimOfferAudience } from './ClaimOffer';

export const OfferAgreementDetail = ({
  agreement,
  audience,
  children,
}: {
  agreement: NonNullable<NonUndefined<ClaimOfferMinimalDetail['agreement']>>;
  audience: ClaimOfferAudience;
  children?: React.ReactNode;
}) => {
  const t = useTranslations();
  const fieldT = useTranslations('claim.negotiation.offer_card.agreement');
  const isInsurer = audience === 'insurer';
  const signedDocuments = agreement.signed_documents ?? [];

  return (
    <div>
      <Separator />
      <div className='flex items-center gap-2 p-4'>
        <IconSignature className='h-5 w-5 shrink-0 text-stone-500' />
        <p className='text-lg font-semibold text-stone-900'>
          {fieldT('title')}
        </p>
      </div>
      <Separator />

      <OfferDetailGrid>
        <OfferDetailField label={fieldT('name')}>
          <OfferDetailValue>{agreement.name}</OfferDetailValue>
        </OfferDetailField>

        <OfferDetailField label={fieldT('provider')}>
          <OfferDetailValue>{agreement.provider_type}</OfferDetailValue>
        </OfferDetailField>

        <OfferDetailField label={fieldT('transaction')}>
          <OfferDetailValue>{agreement.provider_id ?? '—'}</OfferDetailValue>
        </OfferDetailField>

        <OfferDetailField label={fieldT('status')}>
          <OfferDetailValue>
            {t(`offer.agreement.status.${agreement.status}`)}
          </OfferDetailValue>
        </OfferDetailField>

        {agreement.canceled_at && (
          <OfferDetailField label={fieldT('canceled_at')}>
            <OfferDetailValue>
              {format(new Date(agreement.canceled_at), 'Pp')}
            </OfferDetailValue>
          </OfferDetailField>
        )}

        {agreement.expired_at && (
          <OfferDetailField label={fieldT('expired_at')}>
            <OfferDetailValue>
              {format(new Date(agreement.expired_at), 'Pp')}
            </OfferDetailValue>
          </OfferDetailField>
        )}

        {/* El motivo de la conformidad puede venir escrito en AutoSign: el
            reclamante solo ve el motivo público del ofrecimiento. */}
        {isInsurer && agreement.canceled_reason && (
          <OfferDetailField label={fieldT('canceled_reason')}>
            <OfferDetailValue>{agreement.canceled_reason}</OfferDetailValue>
          </OfferDetailField>
        )}

        {agreement.rejected_at && (
          <OfferDetailField label={fieldT('rejected_at')}>
            <OfferDetailValue>
              {format(new Date(agreement.rejected_at), 'Pp')}
            </OfferDetailValue>
          </OfferDetailField>
        )}

        {agreement.rejected_reason && (
          <OfferDetailField label={fieldT('rejected_reason')}>
            <OfferDetailValue>{agreement.rejected_reason}</OfferDetailValue>
          </OfferDetailField>
        )}

        {agreement.completed_at && (
          <OfferDetailField label={fieldT('completed_at')}>
            <OfferDetailValue>
              {format(new Date(agreement.completed_at), 'Pp')}
            </OfferDetailValue>
          </OfferDetailField>
        )}

        {signedDocuments.length > 0 ? (
          <OfferDetailField label={fieldT('signed_documents')}>
            {signedDocuments.map((document) => (
              <OfferFileLink
                key={document.id}
                href={document.url}
                name={document.original_name}
              />
            ))}
          </OfferDetailField>
        ) : (
          agreement.signed_documents_pending_at && (
            <OfferDetailField label={fieldT('signed_documents')}>
              <OfferDetailValue>
                {t(
                  'claim.negotiation.agreement_document.signing.documents_pending',
                )}
              </OfferDetailValue>
            </OfferDetailField>
          )
        )}

        {children}
      </OfferDetailGrid>
    </div>
  );
};

export const OfferAgreementCardFooter = ({
  children,
  status,
  className,
}: {
  children: React.ReactNode;
  status: OfferStatus;
  className?: string;
}) => {
  const t = useTranslations();

  return (
    <CardFooter
      className={cn(
        'w-full justify-end items-end space-x-2',
        {
          'max-w-2xl items-start': status !== 'pending',
        },
        className,
      )}
    >
      {children}
    </CardFooter>
  );
};

/**
 * Status colour semantics of the negotiation offers table, shared so the card
 * and the table never drift apart. `cancelled` is the legacy spelling of
 * `canceled` and reads the same.
 */
export const OFFER_STATUS_BADGE_CLASS: Record<string, string> = {
  pending: 'bg-amber-50 text-amber-800 border-amber-200',
  accepted: 'bg-green-50 text-green-800 border-green-200',
  rejected: 'bg-red-50 text-red-800 border-red-200',
  canceled: 'bg-stone-100 text-stone-600 border-stone-200',
  cancelled: 'bg-stone-100 text-stone-600 border-stone-200',
  expired: 'bg-stone-100 text-stone-600 border-stone-200',
};

/** Offers whose amount no longer stands. */
export const isInactiveOfferStatus = (status: OfferStatus) =>
  status === 'canceled' ||
  status === 'cancelled' ||
  status === 'expired' ||
  status === 'rejected';

export const OfferStatusBadge = ({ status }: { status: OfferStatus }) => {
  const t = useTranslations();

  return (
    <Badge variant='outline' className={cn(OFFER_STATUS_BADGE_CLASS[status])}>
      {t(`claims.offer.status.${status}`)}
    </Badge>
  );
};

export const OfferAgreementCardStatus = ({
  status,
}: {
  status: OfferStatus;
}) => {
  return (
    <div className='absolute -top-3 right-0'>
      <OfferStatusBadge status={status} />
    </div>
  );
};

export const OfferAgreementCardActionActor = ({
  date,
  first_name,
  last_name,
}: {
  first_name: string;
  last_name: string;
  date: Date | string;
}) => {
  return (
    <p className='text-muted-foreground text-xs text-left'>
      {first_name} {last_name} - {format(new Date(date!), 'dd/MM/yyyy HH:mm')}
    </p>
  );
};

export const OfferAgreementCard = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  return <Card className='relative overflow-none'>{children}</Card>;
};

export const OfferAgreementCardContent = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  return (
    <CardContent className='grid grid-cols-3 gap-6'>{children}</CardContent>
  );
};

export const OfferAgreementCardIcon = () => {
  return (
    <span className='w-6 h-6'>
      <IconMoneybag className='w-6 h-6' />
    </span>
  );
};

export const OfferAgreementCardTitle = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  return (
    <div className='flex flex-row items-center space-x-2'>
      <CardTitle className='uppercase'>{children}</CardTitle>
    </div>
  );
};

export const OfferAgreementCardHeader = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  return (
    <CardHeader className='flex flex-row items-center justify-between'>
      {children}
    </CardHeader>
  );
};

export const OfferAgreementCardAttribute = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  return <div className='flex flex-col space-y-1'>{children}</div>;
};

export const OfferAgreementCardAttributeLabel = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  return <p>{children}</p>;
};

export const OfferAgreementCardDocument = ({
  url,
  name,
  created_by_first_name,
  created_by_last_name,
  accepted_at,
  mimetype,
}: {
  url: string;
  name: string;
  created_by_first_name: string;
  created_by_last_name: string;
  accepted_at?: Date;
  mimetype?: string;
}) => {
  return (
    <div className='w-full space-y-2'>
      <a className='hover:underline !mt-0 italic' target='_blank' href={url}>
        <FileListItem className='border-0 w-full h-6 mr-2 !mt-0'>
          <FileListLabel mimetype={mimetype} className='w-full'>
            {name}
          </FileListLabel>
        </FileListItem>
      </a>
      <p className='text-muted-foreground text-xs text-left'>
        {created_by_first_name} {created_by_last_name} -{' '}
        {format(new Date(accepted_at!), 'dd/MM/yyyy HH:mm')}
      </p>
    </div>
  );
};

export const OfferAgreementCardRejectedReason = ({
  rejected_reason,
  created_by_first_name,
  created_by_last_name,
  rejected_at,
}: {
  rejected_reason: string;
  created_by_first_name: string;
  created_by_last_name: string;
  rejected_at: Date;
}) => {
  return (
    <div className='w-full space-y-2'>
      <p className='text-xs italic'>"{rejected_reason}"</p>
      <p className='text-muted-foreground text-xs text-left'>
        {created_by_first_name} {created_by_last_name} -{' '}
        {format(new Date(rejected_at!), 'dd/MM/yyyy HH:mm')}
      </p>
    </div>
  );
};

export const OfferAgreementCardAttributeValue = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return <span className={cn('font-bold', className)}>{children}</span>;
};
